import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, FileText, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIntelReports } from '@/lib/intelSourceV2';

export default function StickyNav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileIntelOpen, setIsMobileIntelOpen] = useState(false);
  const location = useLocation();
  const { reports } = useIntelReports();

  // Split reports for the dropdown menu
  const caseStudies = reports
    .filter(r => r.type === 'case_study')
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 6);
  const featuredBriefings = reports
    .filter(r => r.type !== 'case_study' && r.pinned)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 4);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <TooltipProvider>
      <header className={cn(
        "sticky top-0 z-50 w-full border-b-2 border-accent transition-all duration-300",
        isScrolled
          ? "bg-[#1A1D21]/95 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
          : "bg-[#1A1D21]/70 backdrop-blur-md"
      )} data-testid="sticky-nav">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center hover:opacity-90 transition-opacity"
            data-testid="nav-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_modernbrutalist/artifacts/ofzw1niq_Options%205-transparent%20background%20landscape%20copy.png" 
              alt="LOGO: DD_CONSULTING // STRATEGIC_ENGINEERING_CONSULTANCY" 
              className="h-20 w-auto object-contain"
              width="240"
              height="80"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-8" role="navigation" aria-label="Main Navigation">
            
            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-heading font-semibold uppercase tracking-wide text-white/80 hover:text-white outline-none">
                Services <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1F232B] border-2 border-[#9e591e] rounded-[2px] mt-2 w-72">
                <DropdownMenuItem asChild className="text-white hover:bg-[#2D323E] hover:text-[#9e591e] font-heading font-bold uppercase cursor-pointer">
                  <Link to="/services">Overview</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#2D323E] hover:text-[#9e591e] font-heading font-bold uppercase cursor-pointer">
                  <Link to="/services/aws">AWS Sovereign Cloud</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#2D323E] hover:text-[#9e591e] font-heading font-bold uppercase cursor-pointer">
                  <Link to="/enterprise-modernisation">Enterprise Modernisation</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#2D323E] hover:text-[#9e591e] font-heading font-bold uppercase cursor-pointer">
                  <Link to="/venture-product-engineering">Venture Product Engineering</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#2D323E]" />
                <DropdownMenuItem asChild className="text-white hover:bg-[#2D323E] hover:text-[#4A90E2] font-heading font-bold uppercase cursor-pointer">
                  <Link to="/ai-maturity-framework" className="w-full flex items-center gap-3" data-testid="nav-services-framework">
                    <span className="text-[#4A90E2]">AI Maturity Framework™</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Intelligence Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={cn(
                  "flex items-center gap-1 text-sm font-mono font-semibold uppercase tracking-wide outline-none transition-all",
                  location.pathname.startsWith('/intelligence')
                    ? "text-white underline decoration-primary decoration-2 underline-offset-4"
                    : "text-white/80 hover:text-white"
                )}
                data-testid="nav-intelligence"
              >
                Intelligence <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="bg-[#1F232B] border-2 border-[#4A90E2] rounded-[2px] mt-2 w-[320px] p-0 max-h-[70vh] overflow-y-auto"
                align="start"
              >
                {/* Header */}
                <DropdownMenuItem asChild className="text-white hover:bg-[#2D323E] hover:text-[#4A90E2] font-heading font-bold uppercase cursor-pointer border-b border-[#2D323E] px-4 py-3">
                  <Link to="/intelligence" className="w-full flex items-center justify-between">
                    <span>Intelligence Hub</span>
                    <span className="font-mono text-[10px] text-gray-500">ALL →</span>
                  </Link>
                </DropdownMenuItem>

                {/* Case Studies */}
                {caseStudies.length > 0 && (
                  <>
                    <DropdownMenuLabel className="font-mono text-[9px] text-[#4A90E2] uppercase tracking-[0.25em] px-4 pt-3 pb-2 flex items-center gap-2">
                      <FileText className="w-3 h-3" /> Case Studies
                    </DropdownMenuLabel>
                    {caseStudies.map((cs) => (
                      <DropdownMenuItem
                        key={cs.id}
                        asChild
                        className="text-white/80 hover:bg-[#2D323E] hover:text-[#4A90E2] cursor-pointer px-4 py-2"
                      >
                        <Link to={`/intelligence/${cs.id}`} className="flex flex-col items-start gap-0.5 w-full">
                          <span className="font-heading font-semibold text-[13px] leading-tight line-clamp-2">
                            {cs.headline || cs.title}
                          </span>
                          {cs.category && (
                            <span className="font-mono text-[9px] text-gray-500 uppercase tracking-wider">
                              {cs.category}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                {/* Featured Briefings */}
                {featuredBriefings.length > 0 && (
                  <>
                    <DropdownMenuSeparator className="bg-[#2D323E]" />
                    <DropdownMenuLabel className="font-mono text-[9px] text-[#9e591e] uppercase tracking-[0.25em] px-4 pt-3 pb-2 flex items-center gap-2">
                      <Radio className="w-3 h-3" /> Featured Briefings
                    </DropdownMenuLabel>
                    {featuredBriefings.map((br) => (
                      <DropdownMenuItem
                        key={br.id}
                        asChild
                        className="text-white/80 hover:bg-[#2D323E] hover:text-[#9e591e] cursor-pointer px-4 py-2"
                      >
                        <Link to={`/intelligence/${br.id}`} className="flex flex-col items-start gap-0.5 w-full">
                          <span className="font-heading font-semibold text-[13px] leading-tight line-clamp-2">
                            {br.headline || br.title}
                          </span>
                          {br.category && (
                            <span className="font-mono text-[9px] text-gray-500 uppercase tracking-wider">
                              {br.category}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                {/* Footer CTAs — tools */}
                <DropdownMenuSeparator className="bg-[#2D323E]" />
                <DropdownMenuItem asChild className="text-white/70 hover:bg-[#2D323E] hover:text-white cursor-pointer px-4 py-3">
                  <Link to="/ai-maturity-framework" className="w-full flex items-center justify-between" data-testid="nav-intel-framework">
                    <span className="font-mono text-[11px] uppercase tracking-widest">AI Maturity Framework™</span>
                    <span className="font-mono text-[10px] text-gray-500 whitespace-nowrap">DIAGNOSTIC →</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white/70 hover:bg-[#2D323E] hover:text-white cursor-pointer px-4 py-3">
                  <Link to="/strategy/build-vs-buy" className="w-full flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-widest">Build vs. Buy Matrix</span>
                    <span className="font-mono text-[10px] text-gray-500">TOOL →</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/about"
              className={cn(
                "text-sm font-heading font-semibold uppercase tracking-wide text-white/80 hover:text-white hover:underline decoration-primary decoration-2 underline-offset-4 transition-all",
                location.pathname === '/about' && "text-white underline"
              )}
              data-testid="nav-about"
            >
              About
            </Link>

            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/ai-maturity-framework"
                  className={cn(
                    "relative text-sm font-heading font-bold uppercase tracking-wide transition-all border rounded px-3 py-1.5 flex items-center gap-2",
                    (location.pathname.startsWith('/ai-maturity-framework') || location.pathname.startsWith('/assessment'))
                      ? "text-white border-[#4A90E2] bg-[#4A90E2]/10"
                      : "text-[#4A90E2] border-[#4A90E2]/40 hover:border-[#4A90E2] hover:bg-[#4A90E2]/10 hover:text-white"
                  )}
                  data-testid="nav-assessment"
                >
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4A90E2] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4A90E2]" />
                  </span>
                  AI Quiz
                </Link>
              </TooltipTrigger>
              <TooltipContent className="bg-[#2D323E] text-white border border-[#4A90E2]">
                <p>5-min AI &amp; Digital Maturity Quiz</p>
              </TooltipContent>
            </Tooltip>

            <Button 
              asChild
              variant="default" 
              className="bg-primary text-white border-white hover:border-primary"
              data-testid="nav-cta-button"
            >
              <Link to="/contact">ENQUIRE</Link>
            </Button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#1A1D21]/98 backdrop-blur-xl border-b-2 border-accent animate-accordion-down" data-testid="mobile-menu">
            <nav className="flex flex-col p-4 space-y-4">
              <span className="text-sm font-mono text-gray-500 uppercase tracking-widest px-2 pt-2">Services</span>
              <Link to="/services" className="text-lg font-heading font-semibold uppercase text-white/90 hover:text-primary pl-4" onClick={handleLinkClick}>Overview</Link>
              <Link to="/services/aws" className="text-lg font-heading font-semibold uppercase text-white/90 hover:text-primary pl-4" onClick={handleLinkClick}>AWS Sovereign Cloud</Link>
              <Link to="/enterprise-modernisation" className="text-lg font-heading font-semibold uppercase text-white/90 hover:text-primary pl-4" onClick={handleLinkClick}>Enterprise Modernisation</Link>
              <Link to="/venture-product-engineering" className="text-lg font-heading font-semibold uppercase text-white/90 hover:text-primary pl-4" onClick={handleLinkClick}>Venture Product Engineering</Link>
              <Link to="/ai-maturity-framework" className="text-lg font-heading font-semibold uppercase text-[#4A90E2] hover:text-white pl-4" onClick={handleLinkClick} data-testid="mobile-nav-services-framework">AI Maturity Framework™</Link>
              
              <div className="h-[1px] bg-white/10 my-2"></div>

              <div>
                <button
                  onClick={() => setIsMobileIntelOpen(o => !o)}
                  className="w-full flex items-center justify-between text-lg font-mono font-semibold uppercase text-white/90 hover:text-primary"
                  data-testid="mobile-nav-intelligence"
                >
                  <span>INTELLIGENCE</span>
                  <ChevronDown className={cn("w-4 h-4 transition-transform", isMobileIntelOpen && "rotate-180")} />
                </button>
                <span className="block text-[10px] text-gray-500 uppercase tracking-wide pl-1 mt-1">
                  Case Studies · Briefings · Tools
                </span>
                {isMobileIntelOpen && (
                  <div className="pl-4 mt-3 space-y-3 border-l-2 border-[#2D323E]">
                    <Link
                      to="/intelligence"
                      className="block text-sm font-heading font-semibold uppercase text-[#4A90E2] hover:text-white"
                      onClick={handleLinkClick}
                    >
                      → All Intelligence
                    </Link>

                    {caseStudies.length > 0 && (
                      <div>
                        <span className="font-mono text-[9px] text-[#4A90E2] uppercase tracking-[0.25em] block mb-2">Case Studies</span>
                        <div className="space-y-2">
                          {caseStudies.map(cs => (
                            <Link
                              key={cs.id}
                              to={`/intelligence/${cs.id}`}
                              className="block text-sm text-white/80 hover:text-[#4A90E2] leading-snug line-clamp-2"
                              onClick={handleLinkClick}
                            >
                              {cs.headline || cs.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {featuredBriefings.length > 0 && (
                      <div>
                        <span className="font-mono text-[9px] text-[#9e591e] uppercase tracking-[0.25em] block mb-2">Featured Briefings</span>
                        <div className="space-y-2">
                          {featuredBriefings.map(br => (
                            <Link
                              key={br.id}
                              to={`/intelligence/${br.id}`}
                              className="block text-sm text-white/80 hover:text-[#9e591e] leading-snug line-clamp-2"
                              onClick={handleLinkClick}
                            >
                              {br.headline || br.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <span className="font-mono text-[9px] text-gray-500 uppercase tracking-[0.25em] block mb-2">Tools</span>
                      <div className="space-y-2">
                        <Link to="/ai-maturity-framework" className="block text-sm text-white/80 hover:text-[#4A90E2] leading-snug" onClick={handleLinkClick} data-testid="mobile-nav-intel-framework">
                          AI Maturity Framework™
                        </Link>
                        <Link to="/strategy/build-vs-buy" className="block text-sm text-white/80 hover:text-[#4A90E2] leading-snug" onClick={handleLinkClick}>
                          Build vs. Buy Matrix
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <Link to="/about" className="text-lg font-heading font-semibold uppercase text-white/90 hover:text-primary" onClick={handleLinkClick}>About</Link>
              <Link
                to="/ai-maturity-framework"
                className={cn(
                  "text-lg font-heading font-bold uppercase border-2 border-[#4A90E2] rounded px-3 py-2 flex items-center gap-2 hover:bg-[#4A90E2]/10 transition-all",
                  (location.pathname.startsWith('/ai-maturity-framework') || location.pathname.startsWith('/assessment'))
                    ? "text-white bg-[#4A90E2]/10"
                    : "text-[#4A90E2]"
                )}
                onClick={handleLinkClick}
              >
                <span className="relative flex h-2 w-2" aria-hidden="true">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4A90E2] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4A90E2]" />
                </span>
                AI Quiz
              </Link>
              
              <Button 
                asChild
                className="w-full mt-4" 
                onClick={handleLinkClick}
                data-testid="mobile-nav-cta-button"
              >
                <Link to="/contact">ENQUIRE</Link>
              </Button>
            </nav>
          </div>
        )}
      </header>
    </TooltipProvider>
  );
}
